import { distance } from 'fastest-levenshtein';
import { FieldDefinition } from '../types/FieldDefinition';
import { typeMapping } from '../types/FieldMapping';

export interface FieldSimilarityScore {
  field: FieldDefinition;
  score: number;
}

/**
 * Calculate similarity using Levenshtein distance, normalized to 0-1 range
 */
function calculateLevenshteinSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 1;

  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;

  const levenshteinDistance = distance(str1, str2);
  return (maxLength - levenshteinDistance) / maxLength;
}

/**
 * Clean field names by removing common prefixes, suffixes, and special characters
 */
function cleanFieldName(fieldName: string): string {
  return fieldName
    .toLowerCase()
    // Remove special characters and replace with spaces
    .replace(/[_\-.]/g, ' ')
    // Remove extra spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Tokenize field name into individual words
 * Handles snake_case, camelCase, PascalCase, kebab-case, and spaces
 */
function tokenizeFieldName(fieldName: string): string[] {
  return fieldName
    // Split on underscores, hyphens, dots, and spaces
    .split(/[_\-.\s]+/)
    // Split camelCase and PascalCase, handling acronyms
    .flatMap((part) => part.split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/))
    // Convert to lowercase and filter out empty strings
    .map((token) => token.toLowerCase())
    .filter((token) => token.length > 0);
}

/**
 * Calculate word-level similarity between two field names
 * Returns a score based on how many common words they share
 */
function calculateWordSimilarity(source: string, target: string): { score: number; commonWords: string[] } {
  const sourceWords = tokenizeFieldName(source);
  const targetWords = tokenizeFieldName(target);

  if (sourceWords.length === 0 || targetWords.length === 0) {
    return { score: 0, commonWords: [] };
  }

  // Common word synonyms/variations that should be treated as equivalent
  const wordSynonyms: { [key: string]: string[] } = {
    'first': ['given', 'fname'],
    'last': ['surname', 'family', 'lname'],
    'email': ['mail'],
    'phone': ['telephone', 'tel', 'mobile'],
    'address': ['addr', 'street'],
    'number': ['num', 'no', 'nbr'],
    'date': ['dt'],
    'time': ['tm'],
    'identifier': ['id', 'uid', 'uuid'],
    'description': ['desc', 'details'],
    'quantity': ['qty', 'count'],
    'amount': ['amt', 'value']
  };

  // Build a reverse map for faster lookup
  const synonymMap = new Map<string, Set<string>>();
  for (const [canonical, synonyms] of Object.entries(wordSynonyms)) {
    const allVariants = new Set([canonical, ...synonyms]);
    for (const word of allVariants) {
      synonymMap.set(word, allVariants);
    }
  }

  // Find common words (exact matches or synonyms)
  const commonWords = new Set<string>();
  for (const sourceWord of sourceWords) {
    for (const targetWord of targetWords) {
      // Exact match
      if (sourceWord === targetWord) {
        commonWords.add(sourceWord);
      } else {
        const sourceSynonyms = synonymMap.get(sourceWord);
        if (sourceSynonyms && sourceSynonyms.has(targetWord)) {
          commonWords.add(sourceWord);
        }
      }
    }
  }

  // Calculate score based on proportion of common words
  const commonWordCount = commonWords.size;
  const maxWords = Math.max(sourceWords.length, targetWords.length);
  const minWords = Math.min(sourceWords.length, targetWords.length);

  // Boost score if most words match
  const score = commonWordCount / maxWords;
  const boostScore = commonWordCount / minWords;

  // Use the average for a balanced score
  const finalScore = (score + boostScore) / 2;

  return {
    score: Math.min(finalScore, 1.0),
    commonWords: Array.from(commonWords)
  };
}

/**
 * Check for common field abbreviations and patterns
 */
function checkCommonAbbreviations(source: string, target: string): number {
  const abbreviationMap: { [key: string]: string[] } = {
    'email': ['email_address', 'email_addr', 'e_mail', 'mail'],
    'phone': ['phone_number', 'telephone', 'tel', 'mobile'],
    'fname': ['first_name', 'firstname', 'given_name'],
    'lname': ['last_name', 'lastname', 'surname', 'family_name'],
    'addr': ['address', 'street_address', 'street_addr'],
    'zip': ['zipcode', 'postal_code', 'postcode'],
    'dob': ['date_of_birth', 'birth_date', 'birthdate'],
    'id': ['identifier', 'uid', 'uuid', 'user_id'],
    'num': ['number', 'no', 'nbr'],
    'desc': ['description', 'details', 'summary'],
    'amt': ['amount', 'value', 'total'],
    'qty': ['quantity', 'count', 'number']
  };

  const sourceLower = source.toLowerCase();
  const targetLower = target.toLowerCase();

  // Check if either field matches known abbreviations
  for (const [abbrev, fullForms] of Object.entries(abbreviationMap)) {
    if (sourceLower.includes(abbrev) && fullForms.some((form) => targetLower.includes(form))) {
      return 0.8;
    }
    if (targetLower.includes(abbrev) && fullForms.some((form) => sourceLower.includes(form))) {
      return 0.8;
    }
  }

  return 0;
}

/**
 * Recursively extract all leaf fields from a field definition
 * Leaf fields are fields that don't have nested children (object_schema or array with object items)
 */
function extractLeafFields(field: FieldDefinition, parentPath: string = ''): FieldDefinition[] {
  const currentPath = parentPath ? `${parentPath}/${field.name}` : field.name;

  // Check if this field has nested object schema
  if (field.object_schema && field.object_schema.fields && field.object_schema.fields.length > 0) {
    // This is a parent object, recurse into its children
    return field.object_schema.fields.flatMap((childField) =>
      extractLeafFields(childField, currentPath)
    );
  }

  // Check if this field is an array of objects
  if (field.array && field.array.type === 'object' && 'fields' in field.array &&
      field.array.fields && field.array.fields.length > 0) {
    // This is an array of objects, recurse into the object fields
    return field.array.fields.flatMap((childField) =>
      extractLeafFields(childField, currentPath)
    );
  }

  // This is a leaf field - return it with the full path as its name
  return [{
    ...field,
    name: currentPath
  }];
}

function getLeafName(fieldName: string): string {
  const parts = fieldName.split('/');
  return parts[parts.length - 1];
}

/**
 * Calculate similarity score between two fields using multiple techniques
 * Prioritizes leaf field name matching over full path matching
 */
function calculateSimilarity(
  sourceField: FieldDefinition,
  targetField: FieldDefinition
): FieldSimilarityScore {
  let totalScore = 0;
  let totalWeight = 0;

  // Extract leaf names for comparison
  const sourceLeafName = getLeafName(sourceField.name);
  const targetLeafName = getLeafName(targetField.name);

  // 1. Exact leaf name match (highest priority)
  if (sourceLeafName === targetLeafName) {
    const weight = 3.0; // Very high weight
    totalScore += 1.0 * weight;
    totalWeight += weight;
  } else {
    // 2. Word-level similarity on leaf names (high priority)
    const leafWordSimilarity = calculateWordSimilarity(sourceLeafName, targetLeafName);
    if (leafWordSimilarity.score > 0.3) {
      const weight = 2.0; // High weight for leaf words
      totalScore += leafWordSimilarity.score * weight;
      totalWeight += weight;
    }

    // 3. Levenshtein distance on leaf names (high priority)
    const leafNameScore = calculateLevenshteinSimilarity(sourceLeafName, targetLeafName);
    if (leafNameScore > 0.5) {
      const weight = 1.5; // High weight for leaf similarity
      totalScore += leafNameScore * weight;
      totalWeight += weight;
    }

    // 4. Word-level similarity on full paths (lower priority)
    const fullPathWordSimilarity = calculateWordSimilarity(sourceField.name, targetField.name);
    if (fullPathWordSimilarity.score > 0.3) {
      const weight = 0.3; // Much lower weight for full path
      totalScore += fullPathWordSimilarity.score * weight;
      totalWeight += weight;
    }

    // 5. Levenshtein distance on full paths (lower priority)
    const fullPathScore = calculateLevenshteinSimilarity(sourceField.name, targetField.name);
    if (fullPathScore > 0.5) {
      const weight = 0.2; // Much lower weight for full path
      totalScore += fullPathScore * weight;
      totalWeight += weight;
    }
  }

  // 6. Display name similarity (if display names exist)
  if (sourceField.display_name && targetField.display_name) {
    const displayNameScore = calculateLevenshteinSimilarity(
      sourceField.display_name.toLowerCase(),
      targetField.display_name.toLowerCase()
    );
    if (displayNameScore > 0.4) {
      const weight = 0.6;
      totalScore += displayNameScore * weight;
      totalWeight += weight;
    }
  }

  // 7. Fuzzy matching on cleaned leaf names
  const sourceClean = cleanFieldName(sourceLeafName);
  const targetClean = cleanFieldName(targetLeafName);

  if (sourceClean !== sourceLeafName || targetClean !== targetLeafName) {
    const cleanScore = calculateLevenshteinSimilarity(sourceClean, targetClean);
    if (cleanScore > 0.6) {
      const weight = 0.8;
      totalScore += cleanScore * weight;
      totalWeight += weight;
    }
  }

  // 8. Common abbreviations and patterns on leaf names
  const abbreviationScore = checkCommonAbbreviations(sourceLeafName, targetLeafName);
  if (abbreviationScore > 0) {
    const weight = 1.0;
    totalScore += abbreviationScore * weight;
    totalWeight += weight;
  }

  // Calculate final weighted average score
  const finalScore = totalWeight > 0 ? totalScore / totalWeight : 0;

  return {
    field: targetField,
    score: Math.min(finalScore, 1.0)
  };
}

/**
 * Find the best matching fields from the target fields for a given source field
 * Returns an array of fields sorted by similarity score (highest first)
 * Only matches against leaf fields, recursively extracting them from nested structures
 *
 * @param sourceField - The destination field to find matches for
 * @param targetFields - Available source fields to match against
 * @param excludePaths - Optional set of field paths to exclude from matching (e.g., incompatible fields)
 * @returns Array of matching fields sorted by similarity score
 */
export function findBestMatch(
  sourceField: FieldDefinition,
  targetFields: FieldDefinition[],
  excludePaths?: Set<string>,
): FieldDefinition[] {
  if (!sourceField || !targetFields || targetFields.length === 0) {
    return [];
  }

  // Extract all leaf fields from target fields
  let leafFields = targetFields.flatMap((field) => extractLeafFields(field));

  // Filter out excluded paths (e.g., incompatible fields)
  if (excludePaths && excludePaths.size > 0) {
    leafFields = leafFields.filter((field) => !excludePaths.has(field.name));
  }

  if (leafFields.length === 0) {
    return [];
  }

  // Calculate similarity scores for all available fields
  const scores = leafFields.map((targetField) =>
    calculateSimilarity(sourceField, targetField)
  );

  scores.sort((a, b) => b.score - a.score);

  // Return only high-confidence matches (>= 0.4) to ensure quality suggestions
  return scores
    .filter((scoreItem) => scoreItem.score >= 0.4)
    .map((scoreItem) => scoreItem.field);
}

/**
 * Get all similarity scores for debugging purposes
 * Only calculates similarity for leaf fields, recursively extracting them from nested structures
 */
export function getAllSimilarityScores(
  sourceField: FieldDefinition,
  targetFields: FieldDefinition[]
): FieldSimilarityScore[] {
  if (!sourceField || !targetFields || targetFields.length === 0) {
    return [];
  }

  // Extract all leaf fields from target fields
  const leafFields = targetFields.flatMap((field) => extractLeafFields(field));

  const scores = leafFields.map((targetField) =>
    calculateSimilarity(sourceField, targetField)
  );

  return scores.sort((a, b) => b.score - a.score);
}
